/**
 * @module constants/statusTone
 * @description 상태 → 색 톤 매핑의 단일 원천 (SURINP-683).
 *
 * 같은 상태는 어느 화면이든 같은 톤이다. 톤의 실제 색은 variables.css 의 상태 색 토큰 표
 * (`--status-<tone>` 진한 바탕 / `-bg` 연한 바탕 / `-text` 연한 바탕 위 글자)에 있다.
 * 명세(MOBILE-UI-PAGE-SPECS) 5색 밖 상태는 진행 단계로 묶는다.
 *
 * 사용처:
 * - StatusBadge: `rd-status-badge--<tone>` 클래스
 * - RealtimeActivityFeedPanel(대시보드 피드), useSettlementStatus(정산), MobileStatusBadge(모바일 기사)
 * - RepairRequestManagement 모바일 건수, asSearchShared 고객 A/S 조회 상태 글자색
 */

/**
 * 색 톤.
 * - receipt: 파랑(접수) · assign: 보라(배정) · visit: 황색(방문예정) · complete: 초록(완료)
 * - danger: 빨강(취소·지연) · neutral: 회색(그 밖)
 *
 * @typedef {'receipt'|'assign'|'visit'|'complete'|'danger'|'neutral'} StatusTone
 */

/**
 * 접수·상담 상태 한글명 → 톤.
 * 방문확정대기는 배정 뒤 방문 확정 전이라 배정 계열, 수리중·재방문예정은 방문 단계라 황색.
 *
 * @type {Record<string, StatusTone>}
 */
export const REPAIR_STATUS_TONE = {
  접수: 'receipt',
  배정: 'assign',
  방문확정대기: 'assign',
  방문예정: 'visit',
  재방문예정: 'visit',
  수리중: 'visit',
  완료: 'complete',
  확정: 'complete',
  지연: 'danger',
  취소: 'danger',
  상담진행: 'receipt',
  유선완료: 'complete',
  유선상완료: 'complete',
  수리접수: 'assign',
  수리접수완료: 'assign',
  상담취소: 'danger'
}

/**
 * 접수 상태 코드 → 톤.
 *
 * @type {Record<string, StatusTone>}
 */
export const REPAIR_CODE_TONE = {
  R_REQUEST: 'receipt',
  R_ASSIGNMENT: 'assign',
  R_VISIT: 'visit',
  R_REVISIT: 'visit',
  R_REPAIR: 'visit',
  R_COMPLETE: 'complete',
  R_CONFIRM: 'complete',
  R_DELAY: 'danger'
}

/**
 * 대시보드 실시간 피드 type → 톤.
 *
 * @type {Record<string, StatusTone>}
 */
export const FEED_TYPE_TONE = {
  RECEIPT: 'receipt',
  ASSIGN: 'assign',
  VISIT_PLAN: 'visit',
  COMPLETE: 'complete',
  DELAY: 'danger'
}

/**
 * 정산 상태 → 톤. 「다음에 무엇을 기다리나」로 묶는다 —
 * 계산완료·조정완료는 다음 동작이 확인이라 보라, 확인완료는 승인 대기라 황색.
 *
 * @type {Record<string, StatusTone>}
 */
export const SETTLEMENT_STATUS_TONE = {
  CREATED: 'receipt',
  CALCULATED: 'assign',
  ADJUSTED: 'assign',
  CONFIRMED: 'visit',
  APPROVED: 'complete',
  PAID: 'complete',
  DISPUTED: 'danger',
  REJECTED: 'danger',
  CANCELLED: 'neutral'
}

/**
 * 접수 상태(한글명 또는 R_* 코드)의 톤. 모르는 값은 neutral.
 *
 * @param {string} value - 한글 상태명(예: '배정') 또는 상태 코드(예: 'R_ASSIGNMENT')
 * @returns {StatusTone}
 */
export function repairStatusTone(value) {
  return REPAIR_STATUS_TONE[value] || REPAIR_CODE_TONE[value] || 'neutral'
}

/**
 * 대시보드 피드 type 의 톤. 모르는 값은 neutral.
 *
 * @param {string} type - 피드 type (예: 'ASSIGN')
 * @returns {StatusTone}
 */
export function feedTypeTone(type) {
  return FEED_TYPE_TONE[type] || 'neutral'
}

/**
 * 정산 상태의 톤. 모르는 값은 neutral.
 *
 * @param {string} status - 정산 상태 코드 (예: 'CALCULATED')
 * @returns {StatusTone}
 */
export function settlementStatusTone(status) {
  return SETTLEMENT_STATUS_TONE[status] || 'neutral'
}

/**
 * 흰 바탕 위 상태 글자색 CSS 변수 참조 — 인라인 style 의 color 값으로 쓴다.
 *
 * @param {StatusTone} tone
 * @returns {string} 예: 'var(--status-assign-text)'
 */
export function statusTextColorVar(tone) {
  return `var(--status-${tone}-text)`
}
